/**
 * Admin → Voice → Phone Numbers tab.
 *
 * Multi-tenant voice inbound v1 (2026-07-17). CRUD for per-tenant
 * phone number registrations. Each row represents one E.164 number
 * that this tenant owns; inbound calls to any registered number
 * resolve back to this tenant + optional per-number default agent.
 *
 * Design doc: docs/design/multi-tenant-voice-inbound-v1.md §5.
 */
import React, { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import {
  Button,
  Badge,
  Card,
  Input,
  Modal,
  Select,
  Label,
  Th,
  Td,
} from "../../components/ui";
import { outboundVoiceApi, agentApi } from "../../api/client";

const PURPOSE_LABELS = {
  inbound: "Inbound only",
  outbound: "Outbound only",
  both: "Inbound + outbound",
};

const PURPOSE_COLORS = {
  inbound: COLORS.accent || "#3b82f6",
  outbound: COLORS.warning || "#f59e0b",
  both: COLORS.success || "#10b981",
};

// Exported for unit testing
export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// Exported for unit testing — resolve the display name for a phone number's
// default agent: the agent's name if found, a truncated id if the agent is
// gone from the list, or "(tenant default)" when no agent is pinned.
export function resolveAgentName(row, agents = []) {
  if (!row?.default_agent_id) return "(tenant default)";
  const match = agents.find((a) => a.id === row.default_agent_id);
  return match?.name || row.default_agent_id.slice(0, 8) + "…";
}

export default function PhoneNumbersTab() {
  const [rows, setRows] = useState([]);
  const [quota, setQuota] = useState({ current: 0, quota: 5 });
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await outboundVoiceApi.listPhoneNumbers(true);
      const payload = res?.data || res || {};
      setRows(payload.items || []);
      setQuota(payload.quota || { current: 0, quota: 5 });
    } catch (e) {
      setError(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const res = await agentApi.list();
      const list = res?.data || res || [];
      // Only voice-capable agents are useful as default_agent for a
      // phone number. Fall back to full list if channels metadata
      // isn't in the payload.
      const voiceAgents = list.filter((a) => {
        const chans = a.channels || [];
        if (!chans.length) return true;
        return chans.some(
          (c) => String(c).toLowerCase().includes("voice"),
        );
      });
      setAgents(voiceAgents.length ? voiceAgents : list);
    } catch (e) {
      console.error("agents load failed", e);
    }
  };

  useEffect(() => {
    refresh();
    loadAgents();
  }, []);

  const atQuota = quota.current >= quota.quota;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>Phone numbers</h3>
          <div style={{ marginTop: 4, fontSize: 12, color: COLORS.textSecondary || "#666" }}>
            Register E.164 numbers your tenant owns. Inbound calls to any
            registered number route to this tenant.
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Badge color={atQuota ? (COLORS.warning || "#f59e0b") : (COLORS.textSecondary || "#666")}>
            {quota.current} of {quota.quota} numbers
          </Badge>
          <Button
            onClick={() => setShowAdd(true)}
            disabled={atQuota}
            title={atQuota ? "Quota reached — contact ops to bump" : "Add a phone number"}
          >
            + Add phone number
          </Button>
        </div>
      </div>

      {error && (
        <Card style={{ borderColor: COLORS.error || "#ef4444", color: COLORS.error || "#ef4444" }}>
          {error}
        </Card>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : rows.length === 0 ? (
        <Card>
          <div style={{ textAlign: "center", padding: 24 }}>
            <p style={{ margin: "0 0 12px 0" }}>
              You haven't registered any phone numbers yet.
            </p>
            <Button onClick={() => setShowAdd(true)}>+ Add phone number</Button>
          </div>
        </Card>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <Th>Number</Th>
              <Th>Purpose</Th>
              <Th>Default agent</Th>
              <Th>Status</Th>
              <Th>Added</Th>
              <Th style={{ textAlign: "right" }}>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <PhoneNumberRow
                key={r.id}
                row={r}
                agents={agents}
                onEdit={() => setEditing(r)}
                onVerify={() => setVerifying(r)}
                onDelete={() => setConfirmDelete(r)}
              />
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddPhoneNumberModal
          agents={agents}
          onClose={() => setShowAdd(false)}
          onSaved={async () => {
            setShowAdd(false);
            await refresh();
          }}
        />
      )}

      {editing && (
        <EditPhoneNumberModal
          row={editing}
          agents={agents}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}

      {verifying && (
        <VerifyModal
          row={verifying}
          onClose={() => setVerifying(null)}
          onDone={async () => {
            setVerifying(null);
            await refresh();
          }}
        />
      )}

      {confirmDelete && (
        <Modal
          open
          onClose={() => setConfirmDelete(null)}
          title="Delete phone number?"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await outboundVoiceApi.deletePhoneNumber(confirmDelete.id);
                    setConfirmDelete(null);
                    await refresh();
                  } catch (e) {
                    setError(`Delete failed: ${e.message}`);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? "Deleting…" : "Delete permanently"}
              </Button>
            </>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p>
              Delete <strong>{confirmDelete.phone_number}</strong>?
            </p>
            <p style={{ fontSize: 13, color: COLORS.textSecondary || "#666" }}>
              This is a permanent delete. The number will be released for
              re-registration (by this tenant or another). Historical call
              audit records are preserved (7-year regulatory retention).
              To temporarily disable instead, use "Deactivate" from Edit.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}


function PhoneNumberRow({ row, agents, onEdit, onVerify, onDelete }) {
  const agentName = resolveAgentName(row, agents);

  const purposeColor = PURPOSE_COLORS[row.purpose] || COLORS.textSecondary;

  return (
    <tr>
      <Td>
        <div style={{ fontFamily: "monospace", fontWeight: 500 }}>{row.phone_number}</div>
        {row.label && (
          <div style={{ fontSize: 12, color: COLORS.textSecondary || "#666" }}>{row.label}</div>
        )}
      </Td>
      <Td>
        <Badge color={purposeColor}>{PURPOSE_LABELS[row.purpose] || row.purpose}</Badge>
      </Td>
      <Td>
        <span style={{ fontSize: 13 }}>{agentName}</span>
      </Td>
      <Td>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {row.is_verified ? (
            <Badge color={COLORS.success || "#10b981"}>Verified ✓</Badge>
          ) : (
            <Badge color={COLORS.warning || "#f59e0b"}>Unverified</Badge>
          )}
          {row.is_active ? (
            <Badge color={COLORS.textSecondary || "#666"} style={{ fontSize: 10 }}>Active</Badge>
          ) : (
            <Badge color={COLORS.error || "#ef4444"} style={{ fontSize: 10 }}>Inactive</Badge>
          )}
        </div>
      </Td>
      <Td>
        <span style={{ fontSize: 12, color: COLORS.textSecondary || "#666" }}>
          {fmtDate(row.created_at)}
        </span>
      </Td>
      <Td style={{ textAlign: "right" }}>
        <div style={{ display: "inline-flex", gap: 6 }}>
          {!row.is_verified && (
            <Button variant="secondary" size="sm" onClick={onVerify}>
              Retry verify
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={onEdit}>Edit</Button>
          <Button variant="danger" size="sm" onClick={onDelete}>Delete</Button>
        </div>
      </Td>
    </tr>
  );
}


function AddPhoneNumberModal({ agents, onClose, onSaved }) {
  const [form, setForm] = useState({
    phone_number: "",
    label: "",
    purpose: "both",
    default_agent_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError("");
    setVerifyResult(null);
    try {
      const body = {
        phone_number: form.phone_number.trim(),
        label: form.label.trim() || null,
        purpose: form.purpose,
        provider: "twilio",
        default_agent_id: form.default_agent_id || null,
      };
      const res = await outboundVoiceApi.createPhoneNumber(body);
      const created = res?.data || res || {};
      if (!created.is_verified) {
        setVerifyResult({
          ok: false,
          message: (
            "Number saved, but Twilio verification failed. It won't accept " +
            "inbound calls until verified. Common causes: number not on this " +
            "tenant's Twilio account, wrong auth token, or Twilio API " +
            "unreachable. Fix and click 'Retry verify' on the row."
          ),
        });
      } else {
        await onSaved();
      }
    } catch (e) {
      setError(e.message || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Add phone number"
      width={520}
      footer={
        <>
          {verifyResult && !verifyResult.ok ? (
            <>
              <Button variant="secondary" onClick={onSaved}>
                Close (fix later)
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={busy || !form.phone_number}>
                {busy ? "Adding + verifying…" : "Add number"}
              </Button>
            </>
          )}
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {error && (
          <div style={{ padding: 10, background: "#fef2f2", color: "#dc2626", borderRadius: 6, fontSize: 13 }}>
            {error}
          </div>
        )}
        {verifyResult && (
          <div style={{ padding: 10, background: verifyResult.ok ? "#f0fdf4" : "#fef3c7", color: verifyResult.ok ? "#166534" : "#92400e", borderRadius: 6, fontSize: 13 }}>
            {verifyResult.message}
          </div>
        )}
        <Input
          label="Phone number (E.164)"
          placeholder="+14694020066"
          value={form.phone_number}
          onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
          helper="Include the leading + and country code. Must be a number owned by this tenant's Twilio account."
        />
        <Input
          label="Label (optional)"
          placeholder="Sales hotline"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <div>
          <Label>Purpose</Label>
          <Select
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
          >
            <option value="both">Inbound + outbound</option>
            <option value="inbound">Inbound only</option>
            <option value="outbound">Outbound campaigns only</option>
          </Select>
        </div>
        <div>
          <Label>Default agent (optional)</Label>
          <Select
            value={form.default_agent_id}
            onChange={(e) => setForm({ ...form, default_agent_id: e.target.value })}
          >
            <option value="">(use tenant default voice agent)</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <div style={{ marginTop: 4, fontSize: 12, color: COLORS.textSecondary || "#666" }}>
            If set, inbound calls to this number will be handled by this specific
            agent. If unset, they use the tenant's first live voice agent.
          </div>
        </div>
      </div>
    </Modal>
  );
}


function EditPhoneNumberModal({ row, agents, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: row.label || "",
    purpose: row.purpose,
    default_agent_id: row.default_agent_id || "",
    is_active: row.is_active,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = {
        label: form.label.trim() || null,
        purpose: form.purpose,
        default_agent_id: form.default_agent_id || null,
        is_active: form.is_active,
      };
      await outboundVoiceApi.updatePhoneNumber(row.id, body);
      await onSaved();
    } catch (e) {
      setError(e.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${row.phone_number}`}
      width={520}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {error && (
          <div style={{ padding: 10, background: "#fef2f2", color: "#dc2626", borderRadius: 6, fontSize: 13 }}>
            {error}
          </div>
        )}
        <Input
          label="Label"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <div>
          <Label>Purpose</Label>
          <Select
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
          >
            <option value="both">Inbound + outbound</option>
            <option value="inbound">Inbound only</option>
            <option value="outbound">Outbound campaigns only</option>
          </Select>
        </div>
        <div>
          <Label>Default agent</Label>
          <Select
            value={form.default_agent_id}
            onChange={(e) => setForm({ ...form, default_agent_id: e.target.value })}
          >
            <option value="">(use tenant default voice agent)</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Active</Label>
          <div>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <span style={{ marginLeft: 8, fontSize: 13 }}>
              {form.is_active
                ? "Active — accepts inbound calls"
                : "Inactive — rejects inbound calls (row preserved for audit)"}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}


function VerifyModal({ row, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const runVerify = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await outboundVoiceApi.verifyPhoneNumber(row.id);
      const payload = res?.data || res || {};
      setResult(payload);
    } catch (e) {
      setResult({ is_verified: false, error: e.message || "Verify failed" });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    runVerify();
  }, []);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Verify ${row.phone_number}`}
      width={480}
      footer={
        <>
          <Button variant="secondary" onClick={onDone}>Close</Button>
          {result && !result.is_verified && (
            <Button onClick={runVerify} disabled={busy}>
              {busy ? "Retrying…" : "Retry"}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {busy && <div>Contacting Twilio…</div>}
        {result && result.is_verified && (
          <div style={{ padding: 12, background: "#f0fdf4", color: "#166534", borderRadius: 6 }}>
            ✓ Verified — Twilio confirms this number belongs to your account.
            {result.twilio_pn_sid && (
              <div style={{ marginTop: 6, fontSize: 12, fontFamily: "monospace" }}>
                Twilio PN SID: {result.twilio_pn_sid}
              </div>
            )}
          </div>
        )}
        {result && !result.is_verified && (
          <div style={{ padding: 12, background: "#fef3c7", color: "#92400e", borderRadius: 6, fontSize: 13 }}>
            <strong>Verification failed.</strong>
            <div style={{ marginTop: 6 }}>{result.error || "Unknown error"}</div>
            <div style={{ marginTop: 8, fontSize: 12 }}>
              Common fixes: buy/port the number in Twilio Console first, then
              refresh your Twilio auth token at Admin → Integrations → Twilio.
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
